import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Eye, FileDown, Plus, Trash2 } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { Heading } from '../layout';
import { PillTabBar } from '../reusable';
import { useAuth } from '../../contexts/AuthContext';
import { useUIColors } from '../../hooks/useTheme';
import {
  createMyRequest,
  getMyProfile,
  getMyRequest,
  getMyRequests,
  updateMyPassword,
} from '../../services/profileAPI';
import { downloadRendicionPdf } from '../../utils/rendicionPdf';

const TABS = [
  { key: 'usuario', label: 'Mi Usuario' },
  { key: 'colaborador', label: 'Información Colaborador' },
  { key: 'solicitudes', label: 'Solicitudes' },
];

const REPORT_TYPES = ['Rendición de gastos', 'Fondo por rendir', 'Viático'];
const ITEM_CATEGORIES = [
  'Transporte',
  'Alimentación',
  'Alojamiento',
  'Combustible',
  'Materiales',
  'Servicios',
  'Otro',
];
const ITEM_PAYMENT_METHODS = ['Efectivo', 'Transferencia', 'Tarjeta', 'Cheque', 'Otro'];
const ITEM_TITULARES = ['Personal', 'Soquimat'];
const ITEM_CURRENCIES = ['CLP', 'USD', 'EUR', 'UF'];

const money = (value) =>
  new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const itemAmountClp = (item) => {
  const amount = Number(item.amount || 0);
  const currency = String(item.currency || 'CLP').trim().toUpperCase() || 'CLP';
  if (currency === 'CLP') return amount;
  const rate = Number(item.exchange_rate || 0);
  return amount * (rate > 0 ? rate : 1);
};

const emptyItem = () => ({
  item_date: new Date().toISOString().slice(0, 10),
  category: 'Otro',
  description: '',
  document_number: '',
  issuer_rut: '',
  issuer_name: '',
  payment_method: 'Efectivo',
  titular: 'Personal',
  currency: 'CLP',
  exchange_rate: '1',
  amount: '',
});

const statusTone = (statusRaw, isDarkMode) => {
  const s = String(statusRaw || '').toLowerCase();
  if (s === 'aprobada') {
    return {
      bg: isDarkMode ? '#14532d' : '#dcfce7',
      color: isDarkMode ? '#bbf7d0' : '#166534',
      border: isDarkMode ? '#22c55e' : '#86efac',
    };
  }
  if (s === 'rechazada' || s === 'anulada') {
    return {
      bg: isDarkMode ? '#3f3f46' : '#f4f4f5',
      color: isDarkMode ? '#d4d4d8' : '#52525b',
      border: isDarkMode ? '#52525b' : '#d4d4d8',
    };
  }
  return {
    bg: isDarkMode ? '#1e3a5f' : '#e0f2fe',
    color: isDarkMode ? '#7dd3fc' : '#0369a1',
    border: isDarkMode ? '#0284c7' : '#7dd3fc',
  };
};

const UserProfileWorkspace = ({ isDarkMode = false }) => {
  const { user } = useAuth();
  const ui = useUIColors();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState('usuario');
  const [profile, setProfile] = useState(null);
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [passwordMsg, setPasswordMsg] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isSavingPassword, setIsSavingPassword] = useState(false);

  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestForm, setRequestForm] = useState({
    report_date: new Date().toISOString().slice(0, 10),
    report_type: 'Rendición de gastos',
    description: '',
    notes: '',
    items: [emptyItem()],
  });
  const [requestError, setRequestError] = useState('');
  const [isSavingRequest, setIsSavingRequest] = useState(false);
  const [detailRequest, setDetailRequest] = useState(null);

  useEffect(() => {
    const requested = String(searchParams.get('tab') || '').trim().toLowerCase();
    if (TABS.some((t) => t.key === requested)) {
      setTab(requested);
    }
  }, [searchParams]);

  const inputStyle = {
    height: 34,
    borderRadius: 8,
    border: `1px solid ${ui.inputBorder || ui.border}`,
    background: ui.inputBg || ui.panel,
    color: ui.inputText || ui.text,
    padding: '0 10px',
    fontSize: 13,
    width: '100%',
    boxSizing: 'border-box',
  };
  const primaryBtn = {
    height: 34,
    borderRadius: 8,
    border: 'none',
    background: ui.interactive || '#16a34a',
    color: ui.textOnAccent || '#fff',
    padding: '0 12px',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  };
  const ghostBtn = {
    height: 28,
    borderRadius: 8,
    border: `1px solid ${ui.border}`,
    background: 'transparent',
    color: ui.text,
    padding: '0 8px',
    cursor: 'pointer',
    fontSize: 12,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
  };
  const cardStyle = {
    border: `1px solid ${ui.border}`,
    borderRadius: 12,
    background: ui.panel,
    overflow: 'hidden',
  };

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');
      const profileData = await getMyProfile();
      setProfile(profileData || null);
      try {
        const requestRows = await getMyRequests();
        setRequests(Array.isArray(requestRows) ? requestRows : []);
      } catch (reqErr) {
        setRequests([]);
        setError(reqErr.message || 'No se pudieron cargar las solicitudes.');
      }
    } catch (err) {
      setError(err.message || 'No se pudo cargar el perfil.');
      setProfile(null);
      setRequests([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const profileUser = profile?.user || user || null;
  const collaborator = profile?.collaborator || user?.collaborator || null;
  const roles = Array.isArray(profileUser?.roles) ? profileUser.roles : [];

  const requestTotal = useMemo(
    () => requestForm.items.reduce((acc, item) => acc + itemAmountClp(item), 0),
    [requestForm.items]
  );

  const requestTotalToReturn = useMemo(
    () =>
      requestForm.items.reduce((acc, item) => {
        if (String(item.titular || '').trim() === 'Soquimat') return acc;
        return acc + itemAmountClp(item);
      }, 0),
    [requestForm.items]
  );

  const handlePasswordSave = async () => {
    setPasswordMsg('');
    setPasswordError('');
    if (!passwordForm.current_password || !passwordForm.new_password) {
      setPasswordError('Completa la contraseña actual y la nueva.');
      return;
    }
    if (passwordForm.new_password.length < 8) {
      setPasswordError('La nueva contraseña debe tener al menos 8 caracteres.');
      return;
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setPasswordError('La confirmación no coincide.');
      return;
    }
    try {
      setIsSavingPassword(true);
      await updateMyPassword({
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      });
      setPasswordMsg('Contraseña actualizada.');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      setPasswordError(err.message || 'No se pudo actualizar la contraseña.');
    } finally {
      setIsSavingPassword(false);
    }
  };

  const updateItem = (index, patch) => {
    setRequestForm((prev) => ({
      ...prev,
      items: prev.items.map((item, i) => {
        if (i !== index) return item;
        const next = { ...item, ...patch };
        if (Object.prototype.hasOwnProperty.call(patch, 'currency')) {
          const currency = String(patch.currency || 'CLP').toUpperCase();
          next.currency = currency;
          if (currency === 'CLP') next.exchange_rate = '1';
        }
        return next;
      }),
    }));
  };

  const handleCreateRequest = async () => {
    setRequestError('');
    const items = requestForm.items
      .map((item, index) => ({
        item_date: item.item_date || requestForm.report_date,
        category: item.category || 'Otro',
        description: item.description || '',
        document_number: item.document_number || '',
        issuer_rut: item.issuer_rut || '',
        issuer_name: item.issuer_name || '',
        payment_method: item.payment_method || '',
        titular: item.titular === 'Soquimat' ? 'Soquimat' : 'Personal',
        currency: String(item.currency || 'CLP').toUpperCase() || 'CLP',
        exchange_rate:
          String(item.currency || 'CLP').toUpperCase() === 'CLP'
            ? 1
            : Number(item.exchange_rate || 1),
        amount: Number(item.amount || 0),
        line_order: (index + 1) * 10,
      }))
      .filter((item) => item.amount > 0);
    if (!items.length) {
      setRequestError('Agrega al menos un ítem con monto mayor a 0.');
      return;
    }
    try {
      setIsSavingRequest(true);
      await createMyRequest({
        report_date: requestForm.report_date,
        report_type: requestForm.report_type,
        description: requestForm.description,
        notes: requestForm.notes,
        items,
      });
      setShowRequestForm(false);
      setRequestForm({
        report_date: new Date().toISOString().slice(0, 10),
        report_type: 'Rendición de gastos',
        description: '',
        notes: '',
        items: [emptyItem()],
      });
      await load();
      setTab('solicitudes');
    } catch (err) {
      setRequestError(err.message || 'No se pudo enviar la solicitud.');
    } finally {
      setIsSavingRequest(false);
    }
  };

  const handleDownloadPdf = async (row) => {
    try {
      const full = await getMyRequest(row.id);
      await downloadRendicionPdf(full);
    } catch (err) {
      window.alert(err.message || 'No se pudo generar el PDF.');
    }
  };

  const InfoRow = ({ label, value, last = false }) => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 16,
        padding: '10px 0',
        borderBottom: last ? 'none' : `1px solid ${ui.border}`,
        fontSize: 14,
      }}
    >
      <span style={{ color: ui.muted }}>{label}</span>
      <span style={{ fontWeight: 600, textAlign: 'right', color: ui.text }}>{value || '—'}</span>
    </div>
  );

  if (!user && !profileUser) {
    return (
      <div>
        <Heading level={2} isDarkMode={isDarkMode}>
          Mi Perfil
        </Heading>
        <p style={{ color: ui.muted }}>No hay sesión activa.</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div>
        <Heading level={2} isDarkMode={isDarkMode}>
          Mi Perfil
        </Heading>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: ui.muted }}>
          Tu cuenta, ficha de colaborador y solicitudes enviadas.
        </p>
      </div>

      <div style={cardStyle}>
        <div style={{ padding: '12px 12px 0' }}>
          <PillTabBar
            ariaLabel="Secciones de mi perfil"
            items={TABS.map((item) => ({
              ...item,
              badge: item.key === 'solicitudes' ? requests.length : undefined,
            }))}
            activeKey={tab}
            onChange={setTab}
          />
        </div>

        <div style={{ padding: 20 }}>
          {isLoading ? (
            <p style={{ margin: 0, color: ui.muted }}>Cargando…</p>
          ) : null}
          {!isLoading && error && tab === 'solicitudes' ? (
            <p style={{ margin: '0 0 12px', color: '#dc2626' }}>{error}</p>
          ) : null}
          {!isLoading && error && tab !== 'solicitudes' && !profile ? (
            <p style={{ margin: 0, color: '#dc2626' }}>{error}</p>
          ) : null}

          {!isLoading && profileUser && tab === 'usuario' ? (
            <div style={{ display: 'grid', gap: 20, maxWidth: 640 }}>
              <section>
                <h3 style={{ margin: '0 0 8px', fontSize: 15, color: ui.text }}>Datos de cuenta</h3>
                <div
                  style={{
                    border: `1px solid ${ui.border}`,
                    borderRadius: 10,
                    padding: '4px 16px',
                    background: ui.page || ui.panel,
                  }}
                >
                  <InfoRow label="Código" value={profileUser?.user_code} />
                  <InfoRow label="Nombre" value={profileUser?.full_name} />
                  <InfoRow label="Correo" value={profileUser?.email} />
                  <InfoRow label="Cargo (usuario)" value={profileUser?.job_title} />
                  <InfoRow
                    label="Roles"
                    value={roles.length ? roles.map((r) => r.name || r.code).join(', ') : '—'}
                  />
                  <InfoRow label="Estado" value={profileUser?.status} last />
                </div>
              </section>

              <section>
                <h3 style={{ margin: '0 0 8px', fontSize: 15, color: ui.text }}>Cambiar contraseña</h3>
                <div style={{ display: 'grid', gap: 10, maxWidth: 420 }}>
                  <label style={{ display: 'grid', gap: 6, fontSize: 12, color: ui.muted }}>
                    Contraseña actual
                    <input
                      type="password"
                      value={passwordForm.current_password}
                      onChange={(e) =>
                        setPasswordForm((p) => ({ ...p, current_password: e.target.value }))
                      }
                      style={inputStyle}
                    />
                  </label>
                  <label style={{ display: 'grid', gap: 6, fontSize: 12, color: ui.muted }}>
                    Nueva contraseña
                    <input
                      type="password"
                      value={passwordForm.new_password}
                      onChange={(e) =>
                        setPasswordForm((p) => ({ ...p, new_password: e.target.value }))
                      }
                      style={inputStyle}
                    />
                  </label>
                  <label style={{ display: 'grid', gap: 6, fontSize: 12, color: ui.muted }}>
                    Confirmar nueva contraseña
                    <input
                      type="password"
                      value={passwordForm.confirm_password}
                      onChange={(e) =>
                        setPasswordForm((p) => ({ ...p, confirm_password: e.target.value }))
                      }
                      style={inputStyle}
                    />
                  </label>
                  {passwordError ? (
                    <p style={{ margin: 0, fontSize: 12, color: '#dc2626' }}>{passwordError}</p>
                  ) : null}
                  {passwordMsg ? (
                    <p style={{ margin: 0, fontSize: 12, color: '#15803d' }}>{passwordMsg}</p>
                  ) : null}
                  <div>
                    <button type="button" onClick={handlePasswordSave} disabled={isSavingPassword} style={primaryBtn}>
                      {isSavingPassword ? 'Guardando…' : 'Actualizar contraseña'}
                    </button>
                  </div>
                </div>
              </section>
            </div>
          ) : null}

          {!isLoading && profileUser && tab === 'colaborador' ? (
            <div style={{ maxWidth: 640 }}>
              <h3 style={{ margin: '0 0 8px', fontSize: 15, color: ui.text }}>Ficha de colaborador</h3>
              {!collaborator ? (
                <p style={{ margin: 0, color: ui.muted, fontSize: 13, lineHeight: 1.5 }}>
                  No tienes un colaborador vinculado a tu usuario. Pide a Recursos Humanos que cree tu
                  ficha y la asocie en <strong>Colaboradores</strong>.
                </p>
              ) : (
                <div
                  style={{
                    border: `1px solid ${ui.border}`,
                    borderRadius: 10,
                    padding: '4px 16px',
                    background: ui.page || ui.panel,
                  }}
                >
                  <InfoRow label="Código" value={collaborator.collaborator_code} />
                  <InfoRow label="Nombre" value={collaborator.full_name} />
                  <InfoRow label="RUT" value={collaborator.rut} />
                  <InfoRow label="Cargo" value={collaborator.job_title} />
                  <InfoRow label="Email" value={collaborator.email} />
                  <InfoRow label="Teléfono" value={collaborator.phone} />
                  <InfoRow label="Área" value={collaborator.department} />
                  <InfoRow label="Centro de costo" value={collaborator.cost_center} />
                  <InfoRow
                    label="Fecha ingreso"
                    value={collaborator.hire_date ? String(collaborator.hire_date).slice(0, 10) : '—'}
                  />
                  <InfoRow label="Estado" value={collaborator.status} last />
                </div>
              )}
            </div>
          ) : null}

          {!isLoading && tab === 'solicitudes' ? (
            <div style={{ display: 'grid', gap: 14 }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 10,
                  flexWrap: 'wrap',
                }}
              >
                <div>
                  <h3 style={{ margin: 0, fontSize: 15, color: ui.text }}>Mis solicitudes</h3>
                  <p style={{ margin: '4px 0 0', fontSize: 12, color: ui.muted }}>
                    Envía rendiciones de gastos y revisa su estado de aprobación.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setRequestError('');
                    setShowRequestForm(true);
                  }}
                  style={{ ...primaryBtn, height: 36 }}
                >
                  <Plus size={16} /> Nueva solicitud
                </button>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
                  <thead>
                    <tr>
                      {['Código', 'Tipo', 'Fecha', 'Total', 'A devolver', 'Estado', ''].map((label) => (
                        <th
                          key={label || 'actions'}
                          style={{
                            textAlign:
                              label === 'Total' || label === 'A devolver'
                                ? 'right'
                                : label === 'Estado'
                                  ? 'center'
                                  : 'left',
                            padding: '8px 10px',
                            fontSize: 11,
                            fontWeight: 700,
                            color: ui.muted,
                            borderBottom: `1px solid ${ui.border}`,
                          }}
                        >
                          {label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {requests.length === 0 ? (
                      <tr>
                        <td
                          colSpan={7}
                          style={{ padding: 16, textAlign: 'center', color: ui.muted, fontSize: 13 }}
                        >
                          Aún no has enviado solicitudes.
                        </td>
                      </tr>
                    ) : (
                      requests.map((row) => {
                        const tone = statusTone(row.status, isDarkMode);
                        return (
                          <tr key={row.id}>
                            <td style={{ padding: '9px 10px', fontSize: 12, borderBottom: `1px solid ${ui.border}` }}>
                              <div style={{ fontWeight: 700, color: ui.text }}>{row.report_code}</div>
                              {row.description ? (
                                <div style={{ fontSize: 11, color: ui.muted, marginTop: 2 }}>{row.description}</div>
                              ) : null}
                            </td>
                            <td style={{ padding: '9px 10px', fontSize: 12, borderBottom: `1px solid ${ui.border}`, color: ui.text }}>
                              {row.report_type || '—'}
                            </td>
                            <td style={{ padding: '9px 10px', fontSize: 12, borderBottom: `1px solid ${ui.border}`, color: ui.text }}>
                              {String(row.report_date || '').slice(0, 10)}
                            </td>
                            <td
                              style={{
                                padding: '9px 10px',
                                fontSize: 12,
                                borderBottom: `1px solid ${ui.border}`,
                                textAlign: 'right',
                                fontWeight: 700,
                                color: ui.text,
                              }}
                            >
                              {money(row.total_amount)}
                            </td>
                            <td
                              style={{
                                padding: '9px 10px',
                                fontSize: 12,
                                borderBottom: `1px solid ${ui.border}`,
                                textAlign: 'right',
                                fontWeight: 600,
                                color: ui.text,
                              }}
                            >
                              {money(row.total_to_return != null ? row.total_to_return : row.total_amount)}
                            </td>
                            <td
                              style={{
                                padding: '9px 10px',
                                fontSize: 12,
                                borderBottom: `1px solid ${ui.border}`,
                                textAlign: 'center',
                              }}
                            >
                              <span
                                style={{
                                  display: 'inline-flex',
                                  padding: '2px 8px',
                                  borderRadius: 999,
                                  fontSize: 11,
                                  fontWeight: 700,
                                  background: tone.bg,
                                  color: tone.color,
                                  border: `1px solid ${tone.border}`,
                                }}
                              >
                                {row.status}
                              </span>
                            </td>
                            <td
                              style={{
                                padding: '9px 10px',
                                fontSize: 12,
                                borderBottom: `1px solid ${ui.border}`,
                                textAlign: 'right',
                              }}
                            >
                              <div style={{ display: 'inline-flex', gap: 6, justifyContent: 'flex-end' }}>
                                <button
                                  type="button"
                                  style={ghostBtn}
                                  onClick={() => setDetailRequest(row)}
                                  title="Ver detalle"
                                >
                                  <Eye size={14} /> Ver
                                </button>
                                <button
                                  type="button"
                                  style={ghostBtn}
                                  onClick={() => handleDownloadPdf(row)}
                                  title="Descargar PDF"
                                >
                                  <FileDown size={14} /> PDF
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {showRequestForm ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: ui.overlay || 'rgba(0,0,0,0.45)',
            zIndex: 10000,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <div
            style={{
              width: 560,
              maxWidth: '100%',
              height: '100%',
              background: ui.panel,
              borderLeft: `1px solid ${ui.border}`,
              padding: 20,
              boxSizing: 'border-box',
              display: 'grid',
              gridTemplateRows: 'auto 1fr auto',
              gap: 16,
            }}
          >
            <div>
              <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: ui.text }}>Nueva solicitud</p>
              <p style={{ margin: '4px 0 0', fontSize: 12, color: ui.muted }}>
                Se enviará como {collaborator?.full_name || profileUser?.full_name || 'tu usuario'}
                {collaborator?.rut ? ` · ${collaborator.rut}` : ''}.
              </p>
            </div>

            <div style={{ overflowY: 'auto', display: 'grid', gap: 12, alignContent: 'start' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <label style={{ display: 'grid', gap: 6, fontSize: 12, color: ui.muted }}>
                  Fecha
                  <input
                    type="date"
                    value={requestForm.report_date}
                    onChange={(e) => setRequestForm((p) => ({ ...p, report_date: e.target.value }))}
                    style={inputStyle}
                  />
                </label>
                <label style={{ display: 'grid', gap: 6, fontSize: 12, color: ui.muted }}>
                  Tipo
                  <select
                    value={requestForm.report_type}
                    onChange={(e) => setRequestForm((p) => ({ ...p, report_type: e.target.value }))}
                    style={inputStyle}
                  >
                    {REPORT_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <label style={{ display: 'grid', gap: 6, fontSize: 12, color: ui.muted }}>
                Descripción
                <input
                  value={requestForm.description}
                  onChange={(e) => setRequestForm((p) => ({ ...p, description: e.target.value }))}
                  style={inputStyle}
                  placeholder="Motivo de la solicitud"
                />
              </label>

              <div
                style={{
                  border: `1px solid ${ui.border}`,
                  borderRadius: 10,
                  padding: 12,
                  display: 'grid',
                  gap: 10,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: ui.text }}>Ítems</p>
                  <button
                    type="button"
                    style={ghostBtn}
                    onClick={() =>
                      setRequestForm((p) => ({ ...p, items: [...p.items, emptyItem()] }))
                    }
                  >
                    <Plus size={14} /> Agregar
                  </button>
                </div>
                {requestForm.items.map((item, index) => (
                  <div
                    key={`req-item-${index}`}
                    style={{
                      display: 'grid',
                      gap: 8,
                      padding: 10,
                      borderRadius: 8,
                      border: `1px solid ${ui.border}`,
                      background: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 12, color: ui.muted, fontWeight: 600 }}>Ítem {index + 1}</span>
                      <button
                        type="button"
                        style={{
                          ...ghostBtn,
                          opacity: requestForm.items.length <= 1 ? 0.4 : 1,
                        }}
                        disabled={requestForm.items.length <= 1}
                        onClick={() =>
                          setRequestForm((p) => ({
                            ...p,
                            items: p.items.filter((_, i) => i !== index),
                          }))
                        }
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <label style={{ display: 'grid', gap: 4, fontSize: 11, color: ui.muted }}>
                        Fecha
                        <input
                          type="date"
                          value={item.item_date}
                          onChange={(e) => updateItem(index, { item_date: e.target.value })}
                          style={inputStyle}
                        />
                      </label>
                      <label style={{ display: 'grid', gap: 4, fontSize: 11, color: ui.muted }}>
                        Categoría
                        <select
                          value={item.category}
                          onChange={(e) => updateItem(index, { category: e.target.value })}
                          style={inputStyle}
                        >
                          {ITEM_CATEGORIES.map((cat) => (
                            <option key={cat} value={cat}>
                              {cat}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                    <label style={{ display: 'grid', gap: 4, fontSize: 11, color: ui.muted }}>
                      Descripción
                      <input
                        value={item.description}
                        onChange={(e) => updateItem(index, { description: e.target.value })}
                        style={inputStyle}
                      />
                    </label>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <label style={{ display: 'grid', gap: 4, fontSize: 11, color: ui.muted }}>
                        Nº documento
                        <input
                          value={item.document_number}
                          onChange={(e) => updateItem(index, { document_number: e.target.value })}
                          style={inputStyle}
                        />
                      </label>
                      <label style={{ display: 'grid', gap: 4, fontSize: 11, color: ui.muted }}>
                        Monto
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.amount}
                          onChange={(e) => updateItem(index, { amount: e.target.value })}
                          style={inputStyle}
                        />
                      </label>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <label style={{ display: 'grid', gap: 4, fontSize: 11, color: ui.muted }}>
                        Moneda
                        <select
                          value={item.currency || 'CLP'}
                          onChange={(e) => updateItem(index, { currency: e.target.value })}
                          style={inputStyle}
                        >
                          {ITEM_CURRENCIES.map((currency) => (
                            <option key={currency} value={currency}>
                              {currency}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label style={{ display: 'grid', gap: 4, fontSize: 11, color: ui.muted }}>
                        Cambio a CLP
                        <input
                          type="number"
                          min="0"
                          step="0.0001"
                          value={item.currency === 'CLP' ? '1' : item.exchange_rate}
                          onChange={(e) => updateItem(index, { exchange_rate: e.target.value })}
                          style={inputStyle}
                          disabled={String(item.currency || 'CLP').toUpperCase() === 'CLP'}
                          title="Tipo de cambio para convertir el monto a CLP"
                        />
                      </label>
                    </div>
                    {String(item.currency || 'CLP').toUpperCase() !== 'CLP' && Number(item.amount || 0) > 0 ? (
                      <div style={{ fontSize: 11, color: ui.muted, textAlign: 'right' }}>
                        Equivalente: {money(itemAmountClp(item))}
                      </div>
                    ) : null}
                    <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 8 }}>
                      <label style={{ display: 'grid', gap: 4, fontSize: 11, color: ui.muted }}>
                        RUT emisor
                        <input
                          value={item.issuer_rut}
                          onChange={(e) => updateItem(index, { issuer_rut: e.target.value })}
                          style={inputStyle}
                        />
                      </label>
                      <label style={{ display: 'grid', gap: 4, fontSize: 11, color: ui.muted }}>
                        Nombre emisor
                        <input
                          value={item.issuer_name}
                          onChange={(e) => updateItem(index, { issuer_name: e.target.value })}
                          style={inputStyle}
                        />
                      </label>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <label style={{ display: 'grid', gap: 4, fontSize: 11, color: ui.muted }}>
                        Método de pago
                        <select
                          value={item.payment_method || 'Efectivo'}
                          onChange={(e) => updateItem(index, { payment_method: e.target.value })}
                          style={inputStyle}
                        >
                          {ITEM_PAYMENT_METHODS.map((method) => (
                            <option key={method} value={method}>
                              {method}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label style={{ display: 'grid', gap: 4, fontSize: 11, color: ui.muted }}>
                        Titular
                        <select
                          value={item.titular || 'Personal'}
                          onChange={(e) => updateItem(index, { titular: e.target.value })}
                          style={inputStyle}
                          title="Soquimat suma al total pero no al total a devolver"
                        >
                          {ITEM_TITULARES.map((titular) => (
                            <option key={titular} value={titular}>
                              {titular}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                  </div>
                ))}
                <div style={{ display: 'grid', gap: 4, justifyItems: 'end', color: ui.text }}>
                  <div style={{ fontWeight: 700 }}>Total rendición (CLP): {money(requestTotal)}</div>
                  <div style={{ fontWeight: 600, color: ui.muted }}>
                    Total a devolver (CLP): {money(requestTotalToReturn)}
                  </div>
                </div>
              </div>
              {requestError ? (
                <p style={{ margin: 0, fontSize: 12, color: '#dc2626' }}>{requestError}</p>
              ) : null}
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setShowRequestForm(false)} style={{ ...ghostBtn, height: 34 }}>
                Cancelar
              </button>
              <button type="button" onClick={handleCreateRequest} disabled={isSavingRequest} style={primaryBtn}>
                {isSavingRequest ? 'Enviando…' : 'Enviar solicitud'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {detailRequest ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: ui.overlay || 'rgba(0,0,0,0.45)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
          }}
          onClick={() => setDetailRequest(null)}
        >
          <div
            style={{
              width: 'min(480px, 100%)',
              background: ui.panel,
              borderRadius: 12,
              border: `1px solid ${ui.border}`,
              padding: 20,
              display: 'grid',
              gap: 10,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: ui.text }}>
              {detailRequest.report_code}
            </p>
            <InfoRow label="Tipo" value={detailRequest.report_type} />
            <InfoRow label="Fecha" value={String(detailRequest.report_date || '').slice(0, 10)} />
            <InfoRow label="Total rendición" value={money(detailRequest.total_amount)} />
            <InfoRow
              label="Total a devolver"
              value={money(
                detailRequest.total_to_return != null
                  ? detailRequest.total_to_return
                  : detailRequest.total_amount
              )}
            />
            <InfoRow label="Estado" value={detailRequest.status} />
            <InfoRow label="Descripción" value={detailRequest.description || '—'} />
            {detailRequest.rejection_reason ? (
              <InfoRow label="Motivo rechazo" value={detailRequest.rejection_reason} last />
            ) : (
              <InfoRow label="Ítems" value={detailRequest.items_count || 0} last />
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" onClick={() => handleDownloadPdf(detailRequest)} style={ghostBtn}>
                <FileDown size={14} /> Descargar PDF
              </button>
              <button type="button" onClick={() => setDetailRequest(null)} style={primaryBtn}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default UserProfileWorkspace;
